import React from "react";
import styled from "@emotion/styled";
import { DateTime } from "luxon";
import { lightTheme } from "styles/theme";
import { Flex, Text } from "styles/utility-components";

import { TextLink } from "components/actions/Links";
import { generateProcurementUrl } from "components/notices/utils";
import Alert from "components/ui/alert/Alert";
import Steps, { StepItem } from "components/ui/steps/Steps";
import Tag from "components/ui/tag/Tag";
import TooltipComponent from "components/ui/tooltip/Tooltip";
import { NoticeDetails } from "lib/hooks/api/notices/useNotice";
import { NoticesStagesv2 } from "lib/types/graphQLEnums";
import { tenderStages } from "lib/types/models";
import { isDefined, isOpenEndedDate } from "lib/utils";
import { toHumanRelativeDateFromNow } from "lib/utils/relative_time";
import AttributeDisplay from "./AttributeDisplay";

type Props = {
  notice: NoticeDetails;
};

type TimelineNotNull = NonNullable<NoticeDetails["timeline"]>;
type TimelineContent = TimelineNotNull["preTender"] &
  TimelineNotNull["tender"] &
  TimelineNotNull["award"][0];
type TimelineStep = "preTender" | "tender" | "award";

const procurementStageMapping = {
  PreTender: "preTender",
  Tender: "tender",
  Award: "award",
} as const;
const stepToIndex = {
  preTender: 0,
  tender: 1,
  award: 2,
} as const;

export default function NoticeTimelineV2({ notice }: Props) {
  if (!notice.timeline || !notice.procurementStage.stage) {
    return null;
  }

  const currentStep = procurementStageMapping[notice.procurementStage.stage];
  const currentStepIndex = stepToIndex[currentStep];

  const focusedAwardStep =
    (currentStep === "award" &&
      // Try to match on the award title if current step, if not found, fallback to the first award
      notice.timeline.award.find((award) => award.title === notice.name)) ||
    notice.timeline.award?.[0];

  const stepRender = (step: TimelineStep): StepItem => {
    const timelineStep = (step !== "award" ? notice.timeline?.[step] : focusedAwardStep) as
      | TimelineContent
      | undefined;
    const stepIndex = stepToIndex[step];
    const beforeCurrentStep = currentStepIndex > stepIndex ? true : false;

    if (!timelineStep || timelineStep.estimated === true) {
      const content = beforeCurrentStep === false && timelineStep && (
        <Flex column gap="16px">
          {step === "tender" && (
            <InfoElement
              title="Publish"
              dates={[timelineStep.publishedDate]}
              estimated={timelineStep.estimated}
            />
          )}
          {step === "award" && (
            <>
              <InfoElement
                title="Award"
                dates={notice.timeline?.award.map((award) => award.awardDate) ?? []}
                estimated={timelineStep.estimated}
                multipleDates={{
                  tooltip:
                    "Multiple contracts exist with different dates, we are showing the earliest one",
                  preferredLogic: "earliest",
                }}
              />
              <InfoElement
                title="Start"
                dates={notice.timeline?.award.map((award) => award.startDate) ?? []}
                estimated={timelineStep.estimated}
                multipleDates={{
                  tooltip:
                    "Multiple contracts exist with different dates, we are showing the earliest one",
                  preferredLogic: "earliest",
                }}
              />
              <InfoElement
                title="Expiry"
                dates={notice.timeline?.award.map((award) => award.expiryDate) ?? []}
                estimated={timelineStep.estimated}
                multipleDates={{
                  tooltip:
                    "Multiple contracts exist with different expiry dates. We are showing the soonest one that is in the future. If they are all in the past, we show the most recent expiry.",
                  preferredLogic: "closestFuture",
                }}
              />
            </>
          )}
        </Flex>
      );

      if (currentStep === step) {
        // Should not happen but have found weird OCDS records which means the timeline is blank
        // This fallback is at least a better experience than the timline being completely empty
        const stage = tenderStages[notice.stagev2 ?? NoticesStagesv2.UnknownTender];
        return {
          icon: "timelineCurrent",
          iconProps: {
            color:
              stage.id === NoticesStagesv2.UnknownTender
                ? lightTheme.colors.sysTextInactive
                : stage.color,
          },
          title: (
            <Header
              stageName={stage.label}
              title={notice.name ?? "--"}
              procurementProcessId={notice.procurementStage.procurementProcessId}
              currentStep
              stageNameTooltip={stage.tooltip}
            />
          ),
          content,
        };
      }

      return {
        icon: beforeCurrentStep ? "timelinePastMissing" : "timelineFuture",
        iconProps: {
          color: beforeCurrentStep
            ? lightTheme.colors.sysTextSecondary
            : step === "tender"
              ? tenderStages.OPEN_TENDER.color
              : tenderStages.AWARDED_CONTRACT.color,
        },
        title: (
          <Header
            stageName={step === "preTender" ? "Pre-tender" : step === "tender" ? "Tender" : "Award"}
            title={beforeCurrentStep ? "Not identified" : "--"}
            procurementProcessId={notice.procurementStage.procurementProcessId}
          />
        ),
        content,
      };
    }

    const stageDetails = tenderStages[timelineStep.stage ?? NoticesStagesv2.UnknownTender];
    const awardExtension =
      step === "award"
        ? notice.timeline?.award
            .filter(
              (
                award,
              ): award is TimelineNotNull["award"][0] & {
                extensionDate: string;
                expiryDate: string;
              } => !!award.extensionDate && !!award.expiryDate,
            )
            .sort((a, b) => {
              const dateA = DateTime.fromISO(a.extensionDate);
              const dateB = DateTime.fromISO(b.extensionDate);
              return sortDatesLatest(dateA, dateB);
            })
        : null;

    return {
      icon:
        currentStepIndex === stepIndex
          ? "timelineCurrent"
          : beforeCurrentStep
            ? "timelinePastFound"
            : "timelineFuture",
      iconProps: {
        color:
          stageDetails.id === NoticesStagesv2.UnknownTender
            ? lightTheme.colors.sysTextInactive
            : stageDetails.color,
      },
      title: (
        <Header
          stageName={stageDetails.label}
          title={timelineStep.title ?? ""}
          procurementProcessId={notice.procurementStage.procurementProcessId}
          currentStep={currentStep === step}
          step={step}
          stageNameTooltip={stageDetails.tooltip}
        />
      ),
      content: (
        <Flex column gap="16px">
          {step === "preTender" && (
            <>
              <InfoElement title="Published" dates={[timelineStep.publishedDate]} />
              <InfoElement
                title="Deadline"
                dates={timelineStep.deadlineDates ?? []}
                multipleDates={{
                  tooltip:
                    "Multiple engagement dates exist. If there are any in the future, the soonest one is shown here. If there are none in the future, the most recent date is shown.",
                  preferredLogic: "closestFuture",
                }}
              />
            </>
          )}
          {step === "tender" && (
            <>
              <InfoElement
                title="Published"
                dates={[timelineStep.publishedDate]}
                estimated={timelineStep.estimated}
              />
              <InfoElement title="Clarification" dates={[timelineStep.clarificationDate]} />
              <InfoElement title="Close" dates={[timelineStep.closeDate]} />
            </>
          )}
          {step === "award" && (
            <>
              <InfoElement
                title="Published"
                dates={notice.timeline?.award.map((award) => award.publishedDate) ?? []}
                multipleDates={{
                  tooltip:
                    "Multiple awards exist with different dates, we are showing the earliest one",
                  preferredLogic: "earliest",
                  forceShownDate:
                    currentStep === "award" ? focusedAwardStep?.publishedDate : undefined,
                }}
              />
              <InfoElement
                title="Award"
                dates={notice.timeline?.award.map((award) => award.awardDate) ?? []}
                estimated={timelineStep.estimated}
                multipleDates={{
                  tooltip:
                    "Multiple awards exist with different dates, we are showing the earliest one",
                  preferredLogic: "earliest",
                  forceShownDate: currentStep === "award" ? focusedAwardStep?.awardDate : undefined,
                }}
              />
              <InfoElement
                title="Start"
                dates={notice.timeline?.award.map((award) => award.startDate) ?? []}
                estimated={timelineStep.estimated}
                multipleDates={{
                  tooltip:
                    "Multiple contracts exist with different dates, we are showing the earliest one",
                  preferredLogic: "earliest",
                  forceShownDate: currentStep === "award" ? focusedAwardStep?.startDate : undefined,
                }}
              />
              <InfoElement
                title="Expiry"
                dates={notice.timeline?.award.map((award) => award.expiryDate) ?? []}
                estimated={timelineStep.estimated}
                multipleDates={{
                  tooltip:
                    "Multiple contracts exist with different expiry dates. We are showing the soonest one that is in the future. If they are all in the past, we show the most recent expiry.",
                  preferredLogic: "closestFuture",
                  forceShownDate:
                    currentStep === "award" ? focusedAwardStep?.expiryDate : undefined,
                }}
              />
              {awardExtension?.[0] && (
                <TooltipComponent content="Multiple contracts exist with different extension options. We are showing the one that extends furthest into the future.">
                  <Alert type="warning">
                    Option for extension up to{" "}
                    {DateTime.fromISO(awardExtension[0].extensionDate)
                      .diff(DateTime.fromISO(awardExtension[0].expiryDate))
                      .toFormat("M")}{" "}
                    months.
                  </Alert>
                </TooltipComponent>
              )}
            </>
          )}
        </Flex>
      ),
    };
  };

  return (
    <Flex column gap="16px">
      <Text h2>Timeline</Text>
      <Steps
        direction="vertical"
        spacingBetweenSteps={32}
        items={[stepRender("preTender"), stepRender("tender"), stepRender("award")]}
      />
    </Flex>
  );
}

const Header = ({
  stageName,
  title,
  procurementProcessId,
  currentStep,
  step,
  stageNameTooltip,
}: {
  stageName: string;
  title: string;
  procurementProcessId: string;
  currentStep?: boolean;
  step?: "preTender" | "tender" | "award";
  stageNameTooltip?: string;
}) => (
  <AttributeDisplay
    attribute={stageName}
    attributeConfig={{
      header: true,
      tooltip: stageNameTooltip,
    }}
    customValue={
      <Flex gap="4px" alignItems="baseline">
        <Text ellipsis ellipsisMethod="lineClamp" bold={currentStep} color="sysTextSecondary">
          {step && !currentStep ? (
            <TextLink to={generateProcurementUrl(procurementProcessId, step)}>{title}</TextLink>
          ) : (
            title
          )}
        </Text>
        {currentStep && (
          <HeaderExtra description color="sysTextSecondary">
            (Current page)
          </HeaderExtra>
        )}
      </Flex>
    }
    leftColumnWidth="large"
  />
);

const HeaderExtra = styled(Text)({
  lineHeight: "22px", // To align with other text
  textWrap: "nowrap",
});

const sortDatesEarliest = (date1: DateTime, date2: DateTime) => date1.toMillis() - date2.toMillis();
const sortDatesLatest = (date1: DateTime, date2: DateTime) => date2.toMillis() - date1.toMillis();
const sortDatesClosestFuture = (date1: DateTime, date2: DateTime) => {
  const now = DateTime.now().startOf("day"); // Doing so it shows dates that expiry today as closest future
  if (date1 >= now && date2 >= now) {
    return date1.toMillis() - date2.toMillis();
  }
  if (date1 >= now) {
    return -1;
  }
  if (date2 >= now) {
    return 1;
  }
  return date2.toMillis() - date1.toMillis();
};

const InfoElement = ({
  title,
  dates,
  estimated = false,
  multipleDates,
}: {
  title: string;
  dates: (string | undefined | null)[];
  estimated?: boolean;
  multipleDates?: {
    tooltip: string;
    preferredLogic?: "earliest" | "latest" | "closestFuture";
    forceShownDate?: string | undefined | null;
  };
}) => {
  const cleanedDates = [...new Set(dates.filter(isDefined))];
  if (!cleanedDates.length) {
    return <AttributeDisplay attribute={title} leftColumnWidth="large" />;
  }

  const formattedDates = cleanedDates.map((date) => DateTime.fromISO(date));
  const forcedDate = multipleDates?.forceShownDate
    ? DateTime.fromISO(multipleDates.forceShownDate)
    : undefined;
  const preferredDate =
    forcedDate ??
    formattedDates.sort(
      multipleDates?.preferredLogic === "earliest"
        ? sortDatesEarliest
        : multipleDates?.preferredLogic === "latest"
          ? sortDatesLatest
          : sortDatesClosestFuture,
    )[0];

  return (
    <AttributeDisplay
      attribute={title}
      attributeConfig={{
        tag: estimated
          ? {
              label: "Est.",
              tooltip: "Estimated",
            }
          : undefined,
      }}
      customValue={
        <Flex gap="8px" alignItems="baseline">
          <DateDisplay date={preferredDate} />
          {formattedDates.length > 1 && (
            <Tag label="Multiple dates" tooltip={multipleDates?.tooltip} />
          )}
        </Flex>
      }
      leftColumnWidth="large"
    />
  );
};

export const DateDisplay = ({ date }: { date: DateTime }) => {
  const now = DateTime.now().startOf("day");
  const isOpenEnded = isOpenEndedDate(date.toJSDate());
  const isFuture = date >= now;

  return (
    <Flex gap="8px" alignItems="baseline">
      <Text inline>{isOpenEnded ? "Open-ended" : date.toFormat("dd LLL yyyy")}</Text>
      {!isOpenEnded && isFuture && (
        <Text inline description color="sysTextSecondary" data-testid="relative-time">
          {toHumanRelativeDateFromNow(date.toJSDate())}
        </Text>
      )}
    </Flex>
  );
};
